<template>
  <PageLoading v-if="loading" />
  <ErrorMessage v-else-if="error" />
  <V1ScreenScaffold
    v-else-if="groupSection && groupSection.group"
    :next-disabled="!canContinue"
    data-testid="v1-provider-group"
    @next="handleContinue"
  >
    <h1 class="v1-group__title" data-testid="v1-group-title">
      {{ groupSection.group.title || groupSection.title }}
    </h1>

    <ProviderLogoGrid :buckets="gridBuckets" :selected-key="selectedKey" @select="onGridSelect" />
  </V1ScreenScaffold>
  <NoPayments v-else />

  <Modal
    :model-value="activePopup !== null"
    :title="popupTitle"
    :primary-text="popupPrimaryText"
    :secondary-text="popupSecondaryText || undefined"
    placement="bottom"
    data-testid="v1-group-popup"
    @primary="onPopupPrimary"
    @secondary="onPopupSecondary"
    @update:model-value="onPopupModel"
  >
    <p v-if="activePopup && activePopup.notice">{{ activePopup.notice.body }}</p>
  </Modal>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { Modal } from "@payjoy/design-system";
import V1ScreenScaffold from "@/components/v1/V1ScreenScaffold.vue";
import ProviderLogoGrid, { ProviderGridBucket } from "@/components/providers-redesign/ProviderLogoGrid.vue";
import PageLoading from "@/components/PageLoading.vue";
import ErrorMessage from "@/components/ErrorMessage.vue";
import NoPayments from "@/components/NoPayments.vue";
import { fetchV1PaymentInfo, fetchV1Providers } from "@/helpers/v1TestHelper";
import {
  IV1Availability,
  IV1DrillInProvider,
  IV1ProvidersResponse,
  IV1Section,
  IV1SecondaryAction,
  V1AvailabilityState,
  V1SecondaryActionType,
} from "@/types/IV1Providers";

interface ActivePopup {
  provider: IV1DrillInProvider;
  key: string;
  state: IV1Availability["state"];
  notice: IV1Availability["notice"];
  secondaryAction: IV1SecondaryAction | null;
}

/**
 * v1 provider group drill-in (PYMT-2287) — the sub-screen reached by tapping a
 * group card on the providers list. Renders the group's fee-bucket sections
 * (`group.sections[].providers[]`) as a selectable logo grid, mirroring the legacy
 * `InStoreCash` screen in the new UI. Re-fetches the providers response (needs a
 * session, so `fetchV1PaymentInfo` runs first) and locates the group by the route's
 * `groupId`. Unusable (`blocked`) providers are dimmed but still open their notice.
 */
export default defineComponent({
  name: "V1ProviderGroupView",
  components: { V1ScreenScaffold, ProviderLogoGrid, Modal, PageLoading, ErrorMessage, NoPayments },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t } = useI18n();
    const providers = ref<IV1ProvidersResponse | null>(null);
    const loading = ref<boolean>(true);
    const error = ref<boolean>(false);
    const activePopup = ref<ActivePopup | null>(null);
    const selectedKey = ref<string | null>(null);
    const selectedProvider = ref<IV1DrillInProvider | null>(null);

    const groupId = computed<string>(() => String(route.params.groupId || ""));
    const groupSection = computed<IV1Section | undefined>(
      () => providers.value?.sections.find((s) => s.id === groupId.value && s.group !== null),
    );

    const isBlocked = (provider: IV1DrillInProvider): boolean =>
      provider.availability.state === V1AvailabilityState.Blocked;

    onMounted(async () => {
      try {
        await fetchV1PaymentInfo();
        const amount = typeof route.query.amount === "string" ? route.query.amount : undefined;
        const custom = route.query.custom === "true";
        providers.value = await fetchV1Providers({ amount, custom });
      } catch (fetchError) {
        console.error("[v1] Failed to fetch provider group:", fetchError);
        error.value = true;
      } finally {
        loading.value = false;
      }
    });

    const proceed = (provider: IV1DrillInProvider): void => {
      router.push({
        name: "V1PaymentReferenceByDevice",
        params: { deviceTag: route.params.deviceTag },
        // Carry the flow's amount/days/custom and add the selected provider:
        // `providerId` = routing name, `sectionId` = the drill-in row's id.
        query: { ...route.query, providerId: provider.name, sectionId: provider.id },
      });
    };

    const handleSelectProvider = (provider: IV1DrillInProvider, key: string): void => {
      const { state, notice, secondaryAction } = provider.availability;
      if (state === V1AvailabilityState.Enabled) {
        selectedKey.value = key;
        selectedProvider.value = provider;
        return;
      }
      if (notice) {
        activePopup.value = { provider, key, state, notice, secondaryAction };
      }
    };

    const canContinue = computed<boolean>(() => {
      const state = selectedProvider.value?.availability.state;
      return state === V1AvailabilityState.Enabled || state === V1AvailabilityState.Confirm;
    });

    const gridBuckets = computed<ProviderGridBucket[]>(() =>
      (groupSection.value?.group?.sections ?? []).map((bucket, bIndex) => ({
        label: bucket.label || undefined,
        labelTestId: `v1-group-bucket-${bIndex}`,
        items: bucket.providers.map((provider, pIndex) => ({
          key: `${bIndex}-${pIndex}`,
          logoUrl: provider.logoUrl,
          alt: provider.displayName,
          dimmed: isBlocked(provider),
          testId: `v1-group-provider-${provider.id}`,
        })),
      })),
    );

    const onGridSelect = (key: string): void => {
      const [bIndex, pIndex] = key.split("-").map(Number);
      const provider = groupSection.value?.group?.sections[bIndex]?.providers[pIndex];
      if (provider) handleSelectProvider(provider, key);
    };

    const handleContinue = (): void => {
      if (selectedProvider.value) proceed(selectedProvider.value);
    };

    const onPopupModel = (open: boolean): void => {
      if (!open) activePopup.value = null;
    };

    const onPopupPrimary = (): void => {
      const popup = activePopup.value;
      activePopup.value = null;
      if (popup && popup.state === V1AvailabilityState.Confirm) {
        selectedKey.value = popup.key;
        selectedProvider.value = popup.provider;
      }
    };

    const drillInProviders = computed<IV1DrillInProvider[]>(() =>
      (groupSection.value?.group?.sections ?? []).flatMap((bucket) => bucket.providers),
    );

    const onPopupSecondary = (): void => {
      const popup = activePopup.value;
      if (!popup || !popup.secondaryAction) {
        activePopup.value = null;
        return;
      }
      const action = popup.secondaryAction;
      activePopup.value = null;
      if (action.type === V1SecondaryActionType.OpenProvider && action.providerName) {
        const target = drillInProviders.value.find((p) => p.name === action.providerName);
        if (target) proceed(target);
        return;
      }
      if (action.type === V1SecondaryActionType.RequestExtension) {
        router.push({ name: "ExtensionTypeView", params: { deviceTag: route.params.deviceTag } });
      }
    };

    const popupTitle = computed<string>(() => activePopup.value?.notice?.title || "");
    const popupPrimaryText = computed<string>(() => {
      const popup = activePopup.value;
      if (popup?.state === V1AvailabilityState.Confirm) return t("continue");
      if (popup?.secondaryAction?.type === V1SecondaryActionType.RequestExtension) return t("use-another-method");
      return t("close");
    });
    const popupSecondaryText = computed<string>(() => {
      const popup = activePopup.value;
      if (!popup) return "";
      if (popup.secondaryAction) return popup.secondaryAction.label;
      return popup.state === V1AvailabilityState.Confirm ? t("cancel") : "";
    });

    return {
      t,
      loading,
      error,
      groupSection,
      activePopup,
      selectedKey,
      isBlocked,
      gridBuckets,
      onGridSelect,
      canContinue,
      handleSelectProvider,
      handleContinue,
      onPopupModel,
      onPopupPrimary,
      onPopupSecondary,
      popupTitle,
      popupPrimaryText,
      popupSecondaryText,
    };
  },
});
</script>

<style scoped>
.v1-group__title {
  margin: 0;
  font-family: "Inter", sans-serif;
  font-size: 20px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
}

@media (min-width: 768px) {
  .v1-group__title {
    text-align: center;
  }
}
</style>
